import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { formatArea, formatBudgetRange, propertyTypeLabel, statusLabel } from '@/lib/format';

const EMPTY = {
  title: '', location: '', city: 'Hyderabad', property_type: 'office',
  area_sqft: 0, budget_min: 0, budget_max: 0, status: 'available',
  description: '', amenities: [], images: [], featured: false, contact_person: 'Bhavani Prasad Dornala',
};

export default function AdminPropertiesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [amenityText, setAmenityText] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/properties').then((res) => setItems(res.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const save = async () => {
    const payload = {
      ...form,
      area_sqft: parseInt(form.area_sqft) || 0,
      budget_min: parseInt(form.budget_min) || 0,
      budget_max: parseInt(form.budget_max) || 0,
    };
    try {
      if (editing) {
        await api.put(`/properties/${editing.id}`, payload);
        toast.success('Property updated');
      } else {
        await api.post('/properties', payload);
        toast.success('Property created');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/properties/${deleteId}`);
      toast.success('Deleted');
      setDeleteId(null);
      load();
    } catch (err) { toast.error('Could not delete'); }
  };

  return (
    <div data-testid="admin-properties-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#144726]">Properties</h1>
          <p className="mt-1 text-sm text-slate-500">Manage commercial property listings shown on the public site.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#144726] text-white hover:bg-[#0E3520]" data-testid="admin-add-property-button"><Plus className="mr-2 h-4 w-4" /> New property</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Title</th>
                <th className="px-4 py-3 text-left font-semibold">Location</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>
                <th className="px-4 py-3 text-left font-semibold">Area</th>
                <th className="px-4 py-3 text-left font-semibold">Rent</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={7} className="px-4 py-6 text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-slate-500">No properties yet.</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-[#144726]">{p.title}{p.featured && <Badge className="ml-2 rounded-md bg-[#C6A35B] text-[#144726] border-0">Featured</Badge>}</td>
                  <td className="px-4 py-3 text-slate-600">{p.location}</td>
                  <td className="px-4 py-3 text-slate-600">{propertyTypeLabel(p.property_type)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatArea(p.area_sqft)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatBudgetRange(p.budget_min, p.budget_max)}</td>
                  <td className="px-4 py-3"><Badge className="rounded-md bg-slate-100 text-slate-700 border-0">{statusLabel(p.status)}</Badge></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-2 border-slate-200" onClick={() => openEdit(p)} data-testid="admin-property-edit"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteId(p.id)} data-testid="admin-property-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit property' : 'New property'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="property-form-title" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="property-form-location" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div>
              <Label>Property type</Label>
              <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                <SelectTrigger data-testid="property-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="office">Office</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="coworking">Coworking</SelectItem>
                  <SelectItem value="mixed-use">Mixed-Use</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="leased">Leased</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Area (sqft)</Label><Input type="number" value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} data-testid="property-form-area" /></div>
            <div><Label>Budget Min (₹/sqft)</Label><Input type="number" value={form.budget_min} onChange={(e) => setForm({ ...form, budget_min: e.target.value })} /></div>
            <div><Label>Budget Max (₹/sqft)</Label><Input type="number" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="md:col-span-2">
              <Label>Amenities</Label>
              <div className="flex gap-2">
                <Input value={amenityText} onChange={(e) => setAmenityText(e.target.value)} placeholder="e.g. 24x7 Security" />
                <Button type="button" variant="outline" onClick={() => { if (amenityText.trim()) { setForm({ ...form, amenities: [...form.amenities, amenityText.trim()] }); setAmenityText(''); } }}>Add</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">{form.amenities.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700">{a}<button onClick={() => setForm({ ...form, amenities: form.amenities.filter((_, idx) => idx !== i) })}><X className="h-3 w-3" /></button></span>
              ))}</div>
            </div>
            <div className="md:col-span-2">
              <Label>Image URLs</Label>
              <div className="flex gap-2"><Input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="https://…" /><Button type="button" variant="outline" onClick={() => { if (imgUrl.trim()) { setForm({ ...form, images: [...form.images, imgUrl.trim()] }); setImgUrl(''); } }}><ImageIcon className="mr-2 h-4 w-4" /> Add</Button></div>
              <div className="mt-3 grid grid-cols-3 gap-2">{form.images.map((img, i) => (
                <div key={i} className="relative overflow-hidden rounded-md border border-slate-200"><img src={img} alt="" className="h-20 w-full object-cover" /><button onClick={() => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) })} className="absolute right-1 top-1 rounded bg-white/90 p-1 text-slate-700"><X className="h-3 w-3" /></button></div>
              ))}</div>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input id="featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-[#144726]" />
              <Label htmlFor="featured" className="!mb-0">Show on featured section</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#144726] text-white hover:bg-[#0E3520]" data-testid="property-form-save">{editing ? 'Save changes' : 'Create property'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete property?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
